import * as React from 'react'
import { useVerificationRequestV2 } from './hooks'
import { useVerificationRequestsWidget } from '../atoms/duplicates/hooks'
import { CAlert, CCol } from '@coreui/react-pro'
import { Alert, Button, Card, Col, Flex, Modal, Row, Space, Tag } from 'antd'
import { VerificationEditForm } from '../widgets/VerificationEditForm'
import { VerificationTabs } from '../widgets/VerificationTabs'
import { VerificationClientCard } from '../widgets/VerificationClientCard'
import { VerificationComments } from '../widgets/VerificationComments'
import { RejectReasonModal } from '../widgets/VerificationHistories/components/RejectReasonModal'
import { GoogleChatModal } from '../widgets/GoogleChatModal'
import { Verification } from './types'
import {
  $formDirtyStateV2,
  $isFormDirtyV2,
  approveVerificationV2,
  backToNewVerificationV2,
  getVerificationV2,
  submitApproveV2,
  submitBackToNewV2,
  submitSaveAndApproveV2,
  verificationRequestMountedV2,
} from './model'
import { SystemComment, verificationStatus, verificationType } from '../const'
import { Guard } from '../../../features/session/guard'
import { useUnit } from 'effector-react'
import Title from 'antd/lib/typography/Title'
import { useMemo, useState } from 'react'
import { $history, getHistories } from '../atoms/history/model'

type Props = {
  className?: string
  verificationRequest: Verification
}

const getTagColor = (status: string) => {
  switch (status) {
    case 'new':
      return 'processing'
    case 'accepted':
      return 'success'
    case 'false':
    case undefined:
    case 'rejected':
      return 'error'
    case 'expired':
      return 'warning'
    default:
      return 'processing'
  }
}

export const VerificationRequestEditV2 = ({
  className,
  verificationRequest,
}: Props) => {
  let {
    clientId,
    status,
    firstName,
    middleName,
    lastName,
    dateOfBirth,
    country,
    documentNumber,
    expirationDate,
    systemComment,
  } = verificationRequest

  const { verificationRequest: duplicates } = useVerificationRequestsWidget(
    verificationRequest.requestId,
  )
  const duplicatesCount = duplicates?.length ?? 0

  const isDubCheck =
    systemComment === SystemComment.AUTO_REJECTED_BY_DUPLICATE &&
    status === verificationStatus.verificationRequestStatusRejected()

  return (
    <CCol className={className}>
      <Row gutter={16}>
        <Col span={18}>
          <VerificationTabs
            clientId={clientId}
            requestId={verificationRequest.requestId}
          />
        </Col>
        <Col span={6}>
          <Card className="mb-4" size="small">
            <Title level={2}>Verification</Title>
            {isDubCheck && <Tag color="warning">Dub-check</Tag>}
            {status && (
              <Tag color={getTagColor(status)} className={'mb-1'}>
                {status}
              </Tag>
            )}
            {duplicatesCount > 0 && (
              <Alert
                type="warning"
                showIcon
                message={`Duplicates found: ${duplicatesCount}`}
                style={{ padding: '4px 8px', marginBottom: 8, fontSize: 12 }}
              />
            )}
            <Flex vertical gap="middle">
              <VerificationEditForm
                defaultValues={{
                  firstName,
                  middleName,
                  lastName,
                  country,
                  dateOfBirth,
                  documentNumber,
                  expirationDate,
                }}
                disabled={status !== 'new'}
              />
              <VerificationComments clientId={clientId} />
            </Flex>
          </Card>
        </Col>
      </Row>
    </CCol>
  )
}

export const VerificationRequestEditDetailV2 = () => {
  const [isVisibleModal, setVisibleModal] = useState(false)
  const [isGoogleChatModalVisible, setGoogleChatModalVisible] = useState(false)
  const [expirationWarning, setExpirationWarning] = useState<string | null>(
    null,
  )

  const { verificationRequest, error } = useVerificationRequestV2()

  const history = useUnit($history)
  const historyLoading = useUnit(getHistories.pending)

  const verificationLoading = useUnit(getVerificationV2.pending)
  const approveLoading = useUnit(approveVerificationV2.pending)
  const backToNewLoading = useUnit(backToNewVerificationV2.pending)

  const { requestId, status, systemComment } = verificationRequest

  const isDubCheck =
    systemComment === SystemComment.AUTO_REJECTED_BY_DUPLICATE &&
    status === verificationStatus.verificationRequestStatusRejected()

  const approveWithDuplicateCheck = (onApprove: () => void) => {
    if (verificationRequest.isDuplicate) {
      Modal.confirm({
        title: 'Duplicates found',
        content: 'You are accepting a request with duplicates. Are you sure?',
        okText: 'Yes',
        cancelText: 'Cancel',
        onOk: onApprove,
      })
    } else {
      onApprove()
    }
  }

  const handleAccept = () => {
    const dirtyState = $formDirtyStateV2.getState()
    const expirationFromForm = dirtyState.dirtyFieldKeys.includes(
      'expirationDate',
    )
      ? dirtyState.expirationDate
      : ''
    const effectiveExpiration =
      expirationFromForm || verificationRequest.expirationDate

    if (!effectiveExpiration) {
      setExpirationWarning('You need to fill in the expiration date.')
      return
    }

    if (new Date(effectiveExpiration) < new Date()) {
      setExpirationWarning(
        'The expiration date must not be earlier than the current date.',
      )
      return
    }

    if ($isFormDirtyV2.getState()) {
      const updated = {
        ...verificationRequest,
        ...dirtyState.values,
        expirationDate: effectiveExpiration,
      }
      approveWithDuplicateCheck(() => submitSaveAndApproveV2(updated))
    } else {
      approveWithDuplicateCheck(() => submitApproveV2(verificationRequest))
    }
  }

  const handleReject = () => {
    if ($isFormDirtyV2.getState()) {
      Modal.confirm({
        title: 'Unsaved changes',
        content: 'You have unsaved changes. Continue without saving?',
        okText: 'Continue',
        cancelText: 'Cancel',
        onOk: () => setVisibleModal(true),
      })
    } else {
      setVisibleModal(true)
    }
  }

  const isBackToNewVisible = useMemo(() => {
    let lastRequest = true

    for (let item of history) {
      if (Number(item.requestId) > Number(verificationRequest.requestId)) {
        lastRequest = false
        break
      }
    }

    return (
      verificationRequest.documentType ===
        verificationType.verificationRequestTypeVerification() &&
      !verificationRequest.client.verified &&
      verificationRequest.status !==
        verificationStatus.verificationRequestStatusNew() &&
      lastRequest
    )
  }, [verificationRequest, history])

  return (
    <Space direction="vertical" size="small" style={{ width: '100%' }}>
      {error.message.length > 0 && (
        <CAlert color="danger">Duplicate requests found</CAlert>
      )}
      <Flex gap="large" align="baseline" wrap="wrap" justify="right">
        {!verificationLoading && status && (
          <>
            <Guard permission="approve_verification">
              {status !==
                verificationStatus.verificationRequestStatusAccepted() &&
                !isDubCheck && (
                  <Button
                    type="primary"
                    size="large"
                    style={{
                      backgroundColor: '#2eb85c',
                      borderColor: '#2eb85c',
                    }}
                    onClick={handleAccept}
                    loading={approveLoading}
                  >
                    Accept
                  </Button>
                )}
            </Guard>
            <Guard permission="reject_verification">
              <>
                {status !==
                  verificationStatus.verificationRequestStatusRejected() &&
                  !isDubCheck && (
                    <Button
                      danger
                      size="large"
                      type="primary"
                      onClick={handleReject}
                    >
                      Reject
                    </Button>
                  )}
              </>
            </Guard>
          </>
        )}
        <RejectReasonModal
          open={isVisibleModal}
          onCancel={() => setVisibleModal(false)}
          status={verificationStatus.verificationRequestStatusRejected()}
          requestId={requestId}
          onSuccess={() => verificationRequestMountedV2()}
        />
        {!verificationLoading && !historyLoading && isBackToNewVisible && (
          <Button
            type="primary"
            size="large"
            color="purple"
            variant="solid"
            loading={backToNewLoading}
            onClick={() => submitBackToNewV2(verificationRequest)}
          >
            Back to New
          </Button>
        )}
        <Button
          type="primary"
          size="large"
          style={{ backgroundColor: '#f9b115', borderColor: '#f9b115' }}
          onClick={() => setGoogleChatModalVisible(true)}
        >
          Send to Google Chat
        </Button>
        <Modal
          title="Warning"
          open={!!expirationWarning}
          onOk={() => setExpirationWarning(null)}
          onCancel={() => setExpirationWarning(null)}
          cancelButtonProps={{ style: { display: 'none' } }}
          okText="OK"
        >
          <p>{expirationWarning}</p>
        </Modal>
        <GoogleChatModal
          open={isGoogleChatModalVisible}
          onCancel={() => setGoogleChatModalVisible(false)}
          requestUrl={window.location.href}
          clientId={verificationRequest.clientId}
          clientEmail={verificationRequest.client?.email || ''}
          clientFullName={verificationRequest.client?.fullName || ''}
          managerEmail={verificationRequest.manager?.user?.email || ''}
        />
      </Flex>

      <VerificationClientCard
        client={verificationRequest.client}
        manager={verificationRequest.manager}
        className="mb-4"
        loading={verificationLoading}
        totalBalanceUSD={verificationRequest.totalBalanceUSD}
      />
      <VerificationRequestEditV2 verificationRequest={verificationRequest} />
    </Space>
  )
}
